import { NavLink, Link } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  HiOutlineViewGrid,
  HiOutlineOfficeBuilding,
  HiOutlineUserGroup,
  HiOutlineDocumentText,
  HiOutlineClipboardCheck,
  HiOutlineBell,
  HiOutlineClock,
  HiOutlineCog,
  HiOutlineLogout,
  HiOutlineShieldCheck,
  HiOutlineTemplate,
  HiOutlineBriefcase,
} from 'react-icons/hi';
import { useAuth } from '../../hooks/useAuth';
import { useAuthStore } from '../../store/authStore';
import { HR_ADMIN_ROLES } from '../../constants/roles';
import logo from '../../assets/logo.png';

// Each item can have a `permission` key that maps to a permission in the
// RBAC system.  If omitted, the item is always visible to authenticated users.
// This means enabling a permission in the RBAC page automatically shows the
// corresponding sidebar item — no code change required.
const NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard', icon: HiOutlineViewGrid },
  { to: '/organizations', label: 'Organizations', icon: HiOutlineOfficeBuilding, permission: 'organizations.view' },
  { to: '/employees', label: 'Employees', icon: HiOutlineUserGroup, permission: 'employees.view' },
  { to: '/documents', label: 'Documents', icon: HiOutlineDocumentText, permission: 'documents.view' },
  { to: '/templates', label: 'Templates', icon: HiOutlineTemplate, hrAdminOnly: true },
  { to: '/approvals', label: 'Approvals', icon: HiOutlineClipboardCheck, permission: 'approvals.view' },
  { to: '/notifications', label: 'Notifications', icon: HiOutlineBell, permission: 'notifications.view' },
  { to: '/activity-logs', label: 'Activity Logs', icon: HiOutlineClock, permission: 'activity_logs.view' },
  { to: '/organizational-roles', label: 'Organizational Roles', icon: HiOutlineBriefcase, permission: 'organizational_roles.view' },
  { to: '/settings', label: 'Settings', icon: HiOutlineCog, permission: 'settings.view' },
  { to: '/rbac', label: 'Access Control', icon: HiOutlineShieldCheck, hrAdminOnly: true },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { hasPermission } = useAuthStore();

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (item.hrAdminOnly) return HR_ADMIN_ROLES.includes(user?.role);
    return !item.permission || hasPermission(item.permission);
  });

  const initials = user
    ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() || user.email?.[0]?.toUpperCase()
    : '?';

  return (
    <aside className="flex h-screen w-[260px] flex-shrink-0 flex-col" style={{ backgroundColor: '#171c58' }}>
      {/* Logo */}
      <Link to="/dashboard" className="flex h-20 items-center justify-center px-4">
        <img src={logo} alt="MMPR Group" className="h-14 w-auto object-contain" />
      </Link>

      {/* Navigation */}
      <nav className="mt-2 flex-1 space-y-0.5 overflow-y-auto px-3">
        {visibleItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              clsx(
                'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150',
                isActive
                  ? 'text-white'
                  : 'text-white/60 hover:text-white'
              )
            }
            style={({ isActive }) => isActive
              ? { backgroundColor: 'rgba(255,255,255,0.15)' }
              : undefined
            }
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; }}
            onMouseLeave={(e) => { if (!e.currentTarget.style.backgroundColor.includes('0.15')) e.currentTarget.style.backgroundColor = ''; }}
          >
            {({ isActive }) => (
              <>
                <item.icon
                  className={clsx(
                    'h-5 w-5 flex-shrink-0 transition-colors',
                    isActive ? 'text-white' : 'text-white/60 group-hover:text-white'
                  )}
                />
                {item.label}
                {isActive && (
                  <div className="ml-auto h-1.5 w-1.5 rounded-full bg-white/70" />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User section */}
      <div className="p-3" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="flex items-center gap-3 rounded-lg px-3 py-2">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-semibold text-white">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">
              {user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user?.email}
            </p>
            <p className="truncate text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
              {user?.role?.replace(/_/g, ' ') || 'User'}
            </p>
          </div>
          <button
            onClick={() => logout()}
            className="flex-shrink-0 rounded-md p-1.5 transition-colors hover:bg-white/10"
            style={{ color: 'rgba(255,255,255,0.5)' }}
            title="Logout"
          >
            <HiOutlineLogout className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
